import {
  type QConfigStructure,
  OrderType,
  QBankStructureStatus,
  QConfigStructureType,
  useSearchQBankStructuresLazyQuery,
} from '@exam-management/graphql';
import {
  FormSearchSelection,
  type FormSearchSelectionProps,
  type OptionSelectionProps,
} from '@shared/components';
import { useMemo } from 'react';
import { useFormContext, type FieldValues } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import type { AddQBankToQConfigFormValues } from '../add-qbank-to-qconfig-form';

type FormSearchQBSSelectionProps<TFieldValues extends FieldValues> = Omit<
  FormSearchSelectionProps<TFieldValues>,
  'options'
>;

export default function SearchQBSSelection<TFieldValues extends FieldValues>(
  props: Readonly<FormSearchQBSSelectionProps<TFieldValues>>,
) {
  const formContext = useFormContext<AddQBankToQConfigFormValues>();

  const location = useLocation();
  const structures: QConfigStructure[] = location.state?.structures ?? [];

  const addedQBSIds = useMemo(() => {
    return structures
      .filter((s) => s.type === QConfigStructureType.QBankStructure)
      .map((s) => s.refId);
  }, [structures]);

  const [searchQBS, { data: searchQBSResult, loading: isSearchQBSLoading }] =
    useSearchQBankStructuresLazyQuery({
      variables: {
        search: {
          status: [QBankStructureStatus.Published],
        },
        paging: {
          currentPage: 0,
          pageSize: 200, // REMARK: 200 is temporary solution to get all QBS
        },
        order: {
          field: 'code',
          type: OrderType.Asc,
        },
      },
      fetchPolicy: 'network-only',
    });

  const qbsOptions = useMemo(() => {
    const filteredQBS = (searchQBSResult?.qbsSearchQBankStructures.qBankStructures ?? []).filter(
      (qbs) => !addedQBSIds.includes(qbs.id),
    );

    return (
      filteredQBS.map(
        (qbs) =>
          ({
            label: `${qbs.code}: ${qbs.name}`,
            value: qbs.id,
          }) as OptionSelectionProps,
      ) ?? []
    );
  }, [searchQBSResult, addedQBSIds]);

  return (
    <FormSearchSelection
      {...props}
      label='เลือกคลังข้อสอบ'
      placeholder='เลือกคลังข้อสอบ'
      fullWidth
      options={qbsOptions}
      loading={isSearchQBSLoading}
      onOpen={() => searchQBS()}
      onChange={(...args) => {
        props?.onChange?.(...args);
        formContext.reset({
          ...formContext.getValues(),
          levelItems: {},
        });
      }}
    />
  );
}
